import moment from "moment-timezone";
import { api } from "services/restUtil";
import type { RecurringAvailability, SessionCalendar } from "services/sessions/model";
import { defineApiQuery } from "utils/queries";
import type { SessionSeriesResult } from "./model";
import { getSessionMonthRange } from "./scheduling";
import { searchSessions } from "./lesson";
import { getInvoiceEligibleSessions } from "./utils";

type DateRangeParams = {
  from: string;
  to: string;
};

type AggregatedAvailabilityResponse = {
  list: string[];
};

type StudentBookingCalendarParams = DateRangeParams & {
  instructor_id: string;
  // An on-behalf caller must skip their own GET /sessions: it marks the wrong person's days, and
  // for an admin it 403s, which the global auth handler reads as a session expiry and reloads.
  skipOwnSessions?: boolean;
};

type StudentBookingCalendar = {
  scheduledSlotStarts: string[];
  availableSlotStarts: string[];
};

type FetchTeacherCalendarArgs = DateRangeParams & {
  instructor_id?: string;
};

export function fetchTeacherCalendar(args: FetchTeacherCalendarArgs) {
  return Promise.all([
    fetchSessionCalendarRange(args),
    api.get<AggregatedAvailabilityResponse>("/sessions/aggregated", {
      from: args.from,
      to: args.to,
    }),
  ]).then(([resp, aggregatedResp]) => ({
    args,
    resp: {
      ...resp,
      aggregated: aggregatedResp?.list ?? [],
    },
  }));
}

type FetchStudentCalendarArgs = DateRangeParams & {
  instructor_id?: string;
};

export const studentBookingCalendarQuery = defineApiQuery<
  StudentBookingCalendar,
  StudentBookingCalendarParams
>({
  key: "studentBookingCalendar",
  queryFn: async ({ params }) => {
    const { from, to, instructor_id, skipOwnSessions } = params;
    const [sessions, aggregated] = await Promise.all([
      skipOwnSessions ? Promise.resolve(null) : fetchSessionCalendarRange({ from, to }),
      api.get<AggregatedAvailabilityResponse>(
        `/instructor/${instructor_id}/sessions/aggregated`,
        { from, to }
      ),
    ]);

    return {
      scheduledSlotStarts: (sessions?.sessions ?? [])
        .filter(session => session.status === "scheduled")
        .map(session => session.scheduled),
      availableSlotStarts: aggregated.list,
    };
  },
});

function fetchSessionCalendarRange({ from, to }: DateRangeParams) {
  return api.get<SessionCalendar>("/sessions", { from, to });
}

type SeriesAvailabilityParams = {
  // A student sends instructor_id (they are the student); a teacher sends student_id (they are
  // the instructor); an admin sends both.
  instructor_id?: string;
  student_id?: string;
  count: number;
  timezone: string;
  starts: string[];
};

export type SlotCoverage = {
  start: string;
  bookable: number;
  total: number;
  // Occurrence starts (UTC ISO) that cannot be booked from this slot.
  skipped?: string[];
};

// A pick's share of the requested weeks: how many are bookable, and which are skipped.
export type PickedSlotCoverage = { bookable: number; skipped: string[] };

type SeriesAvailabilityResult = {
  slots: SlotCoverage[];
};

// For each candidate start slot, how many of a `count`-week series can actually be booked.
export const seriesAvailabilityQuery = defineApiQuery<
  SeriesAvailabilityResult,
  SeriesAvailabilityParams
>({
  key: "seriesAvailability",
  queryFn: ({ params }) =>
    api.post<SeriesAvailabilityParams, SeriesAvailabilityResult>(
      "/sessions/series/availability",
      params
    ),
});

export const seriesDetailQuery = defineApiQuery<
  SessionSeriesResult,
  { series_id: string }
>({
  key: "seriesDetail",
  queryFn: ({ params }) =>
    api.get<SessionSeriesResult>(`/sessions/series/${params.series_id}`),
});

// DEPRECATED
export function fetchStudentCalendar(args: FetchStudentCalendarArgs) {
  return Promise.all([
    fetchSessionCalendarRange(args),
    api.get<AggregatedAvailabilityResponse>(
      `/instructor/${args.instructor_id}/sessions/aggregated`,
      {
        from: args.from,
        to: args.to,
      }
    ),
  ]).then(([resp, aggregatedResp]) => {
    return {
      args,
      resp: {
        ...resp,
        aggregated: aggregatedResp?.list ?? [],
      },
    };
  });
}

type FetchSessionsForMonthCommonArgs = {
  month: moment.Moment;
};

export function fetchSessionsForMonth({ month }: FetchSessionsForMonthCommonArgs) {
  const { from, to } = getSessionMonthRange(month);

  return searchSessions({
    from: from.toISOString(),
    to: to.toISOString(),
  }).then(resp => getInvoiceEligibleSessions(resp.list));
}

export function fetchDefaultAvailability() {
  return api.get<SessionCalendar>("/sessions", {
    from: moment.utc().subtract(1, "day").toISOString(),
    to: moment.utc().add(1, "day").toISOString(),
  });
}

type CheckAvailableArgs = {
  recur?: RecurringAvailability;
  available?: string[];
};

export function addAvailable(args: CheckAvailableArgs) {
  return api.post<CheckAvailableArgs>("/sessions/available", args);
}

export function deleteAvailable(args: CheckAvailableArgs) {
  return api.delete<CheckAvailableArgs>("/sessions/available", args);
}

type CheckBusyArgs = {
  busy: string[];
};

export function addBusy(args: CheckBusyArgs) {
  return api.post<CheckBusyArgs>("/sessions/busy", args);
}

export function deleteBusy(args: CheckBusyArgs) {
  return api.delete<CheckBusyArgs>("/sessions/busy", args);
}

export function hasRecurringAvailabilityChanges(
  recurring?: RecurringAvailability | null
) {
  return (
    !!recurring && Object.values(recurring).some(daySlots => (daySlots?.length ?? 0) > 0)
  );
}
